import { useMemo, useState } from 'react'
import { Map, Palette, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/layout/PageHeader'
import { cn } from '@/lib/utils'
import { CareersAdminHost } from '@/components/legacy/CareersAdminHost'
import { TrustSafetyHost } from '@/components/legacy/TrustSafetyHost'

type SettingsTab = 'safety' | 'themes' | 'roadmap'

export function SettingsPage({ initialTab = 'safety' }: { initialTab?: SettingsTab }) {
  const { permissions } = useAuth()
  const tabs = useMemo(() => ([
    { id: 'safety' as const, label: 'Safety controls', description: 'Platform rules and enforcement defaults', icon: ShieldCheck, show: !!permissions?.platformControls },
    { id: 'themes' as const, label: 'Platform themes', description: 'Publish and schedule shared themes', icon: Palette, show: !!permissions?.manageThemes },
    { id: 'roadmap' as const, label: 'Public roadmap', description: 'Manage roadmap entries and progress', icon: Map, show: !!permissions?.roadmapAdmin },
  ].filter((tab) => tab.show)), [permissions])

  const [selected, setSelected] = useState<SettingsTab>(initialTab)
  const active = tabs.some((tab) => tab.id === selected) ? selected : (tabs[0]?.id || 'safety')

  if (!tabs.length) {
    return <PageHeader eyebrow="Administration" title="Configuration" description="Your role does not include platform configuration permissions." />
  }

  return (
    <div>
      <PageHeader eyebrow="Administration" title="Configuration" description="Platform-wide controls that affect the public clyr experience." />
      <div className="admin-settings-workspace">
        <nav className="admin-settings-nav" aria-label="Configuration sections">
          {tabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => setSelected(tab.id)} className={cn(active === tab.id && 'is-active')}>
              <tab.icon aria-hidden="true" />
              <span><strong>{tab.label}</strong><small>{tab.description}</small></span>
            </button>
          ))}
        </nav>
        <section className="admin-settings-content" aria-live="polite">
          {active === 'safety' && <TrustSafetyHost tab="safety" />}
          {active === 'themes' && <CareersAdminHost tab="themes" />}
          {active === 'roadmap' && <CareersAdminHost tab="roadmap" />}
        </section>
      </div>
    </div>
  )
}
